
interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  /** Disable indexing for this page (e.g. auth pages, private routes) */
  noindex?: boolean;
  /** Article published date (ISO string) */
  datePublished?: string;
  /** Article modified date (ISO string) */
  dateModified?: string;
  /** Author name for article schema */
  authorName?: string;
}

const SITE_NAME = 'PromptShare';
const SITE_URL = 'https://promptshare.pro';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const TWITTER_HANDLE = '@promptshare';

export default function SEO({
  title = `${SITE_NAME} - Discover and Share the Best AI Prompts`,
  description = 'Discover, copy, and share the best AI prompts for Midjourney, ChatGPT, DALL-E, Stable Diffusion, and more.',
  keywords = ['ai prompts', 'prompt engineering', 'midjourney prompts', 'chatgpt prompts', 'dall-e prompts', 'prompt share', 'stable diffusion prompts'],
  image = DEFAULT_IMAGE,
  url = SITE_URL,
  type = 'website',
  noindex = false,
  datePublished,
  dateModified,
  authorName,
}: SEOProps) {
  // Ensure title always includes branding unless it already does
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  // Build Article JSON-LD for prompt detail pages
  const articleSchema = type === 'article' ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: fullTitle,
    description,
    image,
    url,
    datePublished: datePublished || new Date().toISOString(),
    dateModified: dateModified || new Date().toISOString(),
    author: {
      '@type': 'Person',
      name: authorName || SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: DEFAULT_IMAGE,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  } : null;

  return (
    <>
      {/* Title */}
      <title>{fullTitle}</title>

      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Primary Meta Tags */}
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Article JSON-LD structured data */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </>
  );
}
